import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { FileService } from '@app/core/services/file.service';
import { UtilityService } from '@app/core/services/utility.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator } from '@app/modal/state-modal/state-modal.component';
import { CollectionItem, CollectionService } from '@app/services/collection.service';
import { ColorGroupService, ProductColorGroupItem } from '@app/services/color-group.service';
import { BusinesscoItem, LogisticService, ProductCycleItem, UnitItem } from '@app/services/logistic.service';
import { ModelItem, ModelService } from '@app/services/model.service';
import { ProductGroup2Item, ProductGroup3Item, ProductGroup4Item, ProductGroupService } from '@app/services/product-group.service';
import { AssortmentItem, PriceGroupMethodItem, ProductGet, ProductService, ProductTypeItem, ServiceTypeItem } from '@app/services/product.service';
import { distinctUntilChanged, filter, forkJoin, from, map, of, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-product-basic-form',
    templateUrl: './product-basic-form.component.html',
    styleUrls: ['./product-basic-form.component.css'],
})
export class ProductBasicFormComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    public set params(value: ProductGet | undefined) {
        this.setParams(value);
    }
    public get params() {
        return this._params;
    }
    private _params?: ProductGet;

    @Output()
    public paramsChange = new EventEmitter<ProductGet>();

    private setParams(value: ProductGet | undefined) {
        this._params = value;

        if (!!value) {
            this.params$.next(value);
        }
    }

    private destroy$ = new Subject<void>();

    private params$ = new ReplaySubject<ProductGet>(1);

    public units: UnitItem[] = [];

    public companies: BusinesscoItem[] = [];

    public productTypes: ProductTypeItem[] = [];

    public priceGroupMethods: PriceGroupMethodItem[] = [];

    public colorGroups: ProductColorGroupItem[] = [];

    public productGroups: Item[] = [];

    public productGroup2s: ProductGroup2Item[] = [];

    public productGroup3s: ProductGroup3Item[] = [];

    public productGroup4s: ProductGroup4Item[] = [];

    public models: ModelItem[] = [];

    public collections: CollectionItem[] = [];

    public assortments: AssortmentItem[] = [];

    public serviceTypes: ServiceTypeItem[] = [];

    public productCycles: ProductCycleItem[] = [];

    public productImageUrl = '';

    constructor(
        private product: ProductService,
        private colorGroup: ColorGroupService,
        private logistic: LogisticService,
        private productGroup: ProductGroupService,
        private model: ModelService,
        private collection: CollectionService,
        private cdr: ChangeDetectorRef,
        private utility: UtilityService,
        private file: FileService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {}

    public onImagePlaceholderClick(event: MouseEvent) {
        if (!this.params) return;

        if (this.utility.isModifierKeyPressed(event)) {
            this.navigator?.newTab({ to: 'picturebanks', parms: { argtype: 'prod_id', argvalue: this.params.prod_keyno } });
        } else {
            this.navigator?.go({ to: 'picturebanks', parms: { argtype: 'prod_id', argvalue: this.params.prod_keyno } });
        }
    }

    public onChanged(key: keyof ProductGet) {
        if (!this.params) return;

        this.paramsChange.emit(this.params);
    }

    public onProductGroupChanged() {
        if (!this.params) return;

        this.params.prodgrp2_id = '';
        this.params.prodgrp3_id = '';
        this.params.prodgrp4_id = '';
        this.productGroup2s = [];
        this.productGroup3s = [];
        this.productGroup4s = [];
        this.params$.next(this.params);
        this.onChanged('prodgrp_id');
        this.cdr.markForCheck();
    }

    public onProductGroup2Changed() {
        if (!this.params) return;

        this.params.prodgrp3_id = '';
        this.params.prodgrp4_id = '';
        this.productGroup3s = [];
        this.productGroup4s = [];
        this.params$.next(this.params);
        this.onChanged('prodgrp2_id');
        this.cdr.markForCheck();
    }

    public onProductGroup3Changed() {
        if (!this.params) return;

        this.params.prodgrp4_id = '';
        this.productGroup4s = [];
        this.params$.next(this.params);
        this.onChanged('prodgrp3_id');
        this.cdr.markForCheck();
    }

    private loadStaticData() {
        forkJoin({
            units: from(this.logistic.getUnitList()),
            companies: from(this.logistic.getCompanies()),
            productTypes: from(this.product.getProductTypes()),
            priceGroupMethods: from(this.product.getPriceGroupMethods()),
            productGroups: from(this.product.getProductGroups()),
            models: from(this.model.getModels()),
            collections: from(this.collection.getCollections({ isactive: '1' })),
            assortments: from(this.product.getAssortments()),
            serviceTypes: from(this.product.getServiceTypes()),
            productCycles: from(this.logistic.getProductCycles()),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                this.units = result.units;
                this.companies = result.companies;
                this.productTypes = result.productTypes;
                this.priceGroupMethods = result.priceGroupMethods;
                this.productGroups = result.productGroups;
                this.models = result.models;
                this.collections = result.collections;
                this.assortments = result.assortments;
                this.serviceTypes = result.serviceTypes;
                this.productCycles = result.productCycles;
                this.cdr.markForCheck();
            });
    }

    private setupParamsSubscriptions() {
        this.params$
            .pipe(
                map((params) => params.prod_id),
                distinctUntilChanged(),
                switchMap((prod_id) => from(this.colorGroup.getProductColorGroups({ prod_id }))),
                takeUntil(this.destroy$),
            )
            .subscribe((colorGroups) => {
                this.colorGroups = colorGroups;
                this.cdr.markForCheck();
            });

        this.params$
            .pipe(
                map((params) => params.prodgrp_id),
                distinctUntilChanged(),
                switchMap((prodgrp_id) => (prodgrp_id > '' ? from(this.productGroup.getProductGroup2s({ prodgrp_id })) : of<ProductGroup2Item[]>([]))),
                takeUntil(this.destroy$),
            )
            .subscribe((productGroup2s) => {
                this.productGroup2s = productGroup2s;
                this.cdr.markForCheck();
            });

        this.params$
            .pipe(
                map((params) => params.prodgrp2_id),
                distinctUntilChanged(),
                switchMap((prodgrp2_id) => (prodgrp2_id > '' ? from(this.productGroup.getProductGroup3s({ prodgrp2_id })) : of<ProductGroup3Item[]>([]))),
                takeUntil(this.destroy$),
            )
            .subscribe((productGroup3s) => {
                this.productGroup3s = productGroup3s;
                this.cdr.markForCheck();
            });

        this.params$
            .pipe(
                map((params) => params.prodgrp3_id),
                distinctUntilChanged(),
                switchMap((prodgrp3_id) => (prodgrp3_id > '' ? from(this.productGroup.getProductGroup4s({ prodgrp3_id })) : of<ProductGroup4Item[]>([]))),
                takeUntil(this.destroy$),
            )
            .subscribe((productGroup4s) => {
                this.productGroup4s = productGroup4s;
                this.cdr.markForCheck();
            });

        this.params$
            .pipe(
                map((params) => params.product_main_image),
                distinctUntilChanged(),
                filter((product_main_image) => typeof product_main_image === 'string' && product_main_image > '' && !product_main_image.startsWith('http')),
                switchMap((product_main_image) => (product_main_image > '' ? from(this.file.getFileInfo({ selectOnlyFromFiles: true, files: [{ fileName: product_main_image }] })) : of(null))),
                takeUntil(this.destroy$),
            )
            .subscribe((result) => {
                if (!!result && typeof result === 'object' && 'files' in result && result.files.length > 0) {
                    // We only want first image for the first version of this feature
                    var fileData = result.files[0];
                    var thumbUrl = fileData.thumbUrl;

                    var decoded = decodeURIComponent(thumbUrl);

                    var qsIndex = decoded.indexOf('{');

                    var url = decoded.substring(0, qsIndex);

                    var qs = decoded.substring(qsIndex);

                    qs = qs.substring(0, qs.indexOf(',"width"')) + ',"width":800,"height":600}';
                    qs = encodeURIComponent(qs);

                    // Insert link to image for use in HTML
                    this.productImageUrl = url + qs;
                }
                this.cdr.markForCheck();
            });
    }

    ngOnInit(): void {
        this.loadStaticData();
        this.setupParamsSubscriptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['params']) {
            this.setParams(changes['params'].currentValue);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
